import { Timestamp } from 'firebase/firestore';

export type TargetInvestorProgress =
  | 'not_contacted'
  | 'outreach_sent'
  | 'contact_established'
  | 'information_requested'
  | 'initial_interest'
  | 'meeting_scheduled'
  | 'meeting_completed'
  | 'under_review'
  | 'due_diligence'
  | 'terms_discussion'
  | 'committed'
  | 'invested'
  | 'passed'
  | 'unresponsive'
  | 'on_hold';

export type TargetInvestorLegacyOutreachStatus =
  | 'not_sent'
  | 'linkedin_request'
  | 'email_sent'
  | 'more_info'
  | 'interested'
  | 'in_play'
  | 'investing';

/** Backward-compatible stored shape during the IP02 migration window. */
export type TargetInvestorOutreachStatus =
  | TargetInvestorProgress
  | TargetInvestorLegacyOutreachStatus;

export type TargetInvestorSyncStatus = 'pending' | 'synced' | 'failed';
export const TARGET_INVESTOR_NEXT_ACTION_OPTIONS = [
  { value: 'research_contact', label: 'Research' },
  { value: 'send_linkedin_request', label: 'LI Request' },
  { value: 'send_introduction_email', label: 'Intro email' },
  { value: 'send_again', label: 'Send Again' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'send_more_information', label: 'More Info' },
  { value: 'arrange_introductory_call', label: 'Arrange Call' },
  { value: 'send_pitch_deck', label: 'Pitch Deck' },
  { value: 'arrange_founder_meeting', label: 'Team Meeting' },
  { value: 'provide_data_room_access', label: 'DR Access' },
  { value: 'confirm_interest', label: 'Confirm' },
  { value: 'discuss_investment_terms', label: 'Terms' },
  { value: 'no_further_action', label: 'Waiting' },
] as const;
export type TargetInvestorNextAction = (typeof TARGET_INVESTOR_NEXT_ACTION_OPTIONS)[number]['value'];

export const TARGET_INVESTOR_RELATIONSHIP_KINDS = [
  'client',
  'volocean',
  'syndicate',
  'unassigned',
] as const;
export type TargetInvestorRelationshipKind = (typeof TARGET_INVESTOR_RELATIONSHIP_KINDS)[number];

export interface TargetInvestorSourceSelection {
  optionId: string;
  labelSnapshot: string;
}

export interface TargetInvestorRelationship {
  kind: TargetInvestorRelationshipKind;
  syndicateId?: string;
  label?: string;
}

export type TargetInvestorOperationalField =
  | 'introducedBySource'
  | 'relationship'
  | 'nextAction';

export type TargetInvestorSourceOptionStatus = 'active' | 'retired';

export interface TargetInvestorSourceOption {
  id: string;
  label: string;
  normalizedLabel: string;
  status: TargetInvestorSourceOptionStatus;
  createdAt: Timestamp;
  createdBy: string;
  updatedAt: Timestamp;
  updatedBy: string;
  retiredAt?: Timestamp | null;
  retiredBy?: string | null;
}

export interface TargetInvestorFieldOptionsDocument {
  sourceOptions: TargetInvestorSourceOption[];
  updatedAt?: Timestamp;
  updatedBy?: string;
}

export interface TargetInvestorFieldOptionsEvent {
  id: string;
  clientId: string;
  type: 'add' | 'rename' | 'retire';
  optionId: string;
  previousLabel: string | null;
  newLabel: string | null;
  previousStatus: TargetInvestorSourceOptionStatus | null;
  newStatus: TargetInvestorSourceOptionStatus;
  createdAt: Timestamp;
  createdBy: string;
}

export const TARGET_INVESTOR_RAISE_CYCLES = [
  'Pre-Seed',
  'Seed',
  'Series A',
  'Series B',
  'Series C+',
  'Bridge',
  'Rolling',
] as const;
export type TargetInvestorRaiseCycle = (typeof TARGET_INVESTOR_RAISE_CYCLES)[number];

export interface TargetInvestorMasterSnapshot {
  person: string;
  company: string;
  email: string | null;
  linkedIn: string | null;
  owner: string;
  type: string;
  location?: string | null;
}

export interface ClientRaiseTargetInvestor {
  id: string;
  clientId: string;
  masterInvestorId: string;
  masterSnapshot: TargetInvestorMasterSnapshot;
  overrides: {
    owner?: string;
    location?: string | null;
  };
  target: {
    raiseCycle?: TargetInvestorRaiseCycle | null;
    outreachStatus: TargetInvestorOutreachStatus;
    raiseAmount?: number | null;
    introducedBySource?: TargetInvestorSourceSelection | null;
    relationship?: TargetInvestorRelationship | null;
    nextAction?: TargetInvestorNextAction | null;
    /** Plain text for this contact on this client list only. Never stored on investors/{id}. */
    personalLine?: string | null;
    /** Second plain-text line for this contact on this client list only. Never stored on investors/{id}. */
    personalLine2?: string | null;
    /** Cold-outreach wave on this client list only. 1 to 9, or null. Never stored on investors/{id}. */
    outreachWave?: number | null;
    /** Admin offset added to the Outlook sent tally. Missing means no offset. */
    sentCountAdjustment?: number | null;
  };
  derived: {
    confidenceStars?: 1 | 2 | 3 | 4 | 5 | null;
    lastContactAt?: Timestamp | null;
    /** Manual-note count only (status/email/system audit lives in outreachEvents). */
    noteCount?: number;
    lastNoteAt?: Timestamp | null;
    lastManualNote?: TargetNoteSummary | null;
  };
  sync: {
    status: TargetInvestorSyncStatus;
    lastSyncedAt?: Timestamp | null;
    lastError?: string;
  };
  metadata: {
    createdAt: Timestamp;
    updatedAt: Timestamp;
    createdBy: string;
    updatedBy: string;
    seededFromMatchAt?: Timestamp;
    seedSourceTier?: 'highConfidence' | 'potential' | 'discovery';
    seedSourceScore?: number;
  };
}

/** Document attached to a note at create time (append-only: never edited afterwards). */
export interface TargetNoteAttachment {
  /** documents registry documentId (clients/{clientId}.documents.allDocuments[]) */
  documentId: string;
  fileName: string;
  /** capitalRaising/{clientId}/documents/{ts}_{safeName} — private path, opened via blob URL */
  storagePath: string;
  fileSize: number;
  fileType: string;
}

/** Denormalized preview of the latest manual note, stored on the parent target investor doc. */
export interface TargetNoteSummary {
  content: string;
  createdAt: Timestamp;
  createdBy?: string | null;
  createdByDisplayName: string | null;
}

export interface TargetNoteEvent {
  id: string;
  targetInvestorId: string;
  clientId: string;
  type: 'manual_note' | 'email_sent' | 'status_change' | 'system';
  content: string;
  metadata?: {
    sourceEventId?: string;
    source?: string;
    actionId?: string;
  };
  createdAt: Timestamp;
  createdBy: string;
  /** Author display name captured at write time; historical notes resolve via userResolver. */
  createdByDisplayName?: string | null;
  /** Attachments set at create time only (documents live in the capital-raising registry). */
  attachments?: TargetNoteAttachment[];
  /** Set when content is edited after create. */
  editedAt?: Timestamp | null;
  editedBy?: string;
  editedByDisplayName?: string | null;
}

export interface OutreachEvent {
  id: string;
  targetInvestorId: string;
  clientId: string;
  type: 'email_sent' | 'linkedin_request' | 'status_change' | 'follow_up' | 'system';
  statusAfter?: string | null;
  occurredAt: Timestamp;
  createdBy: string;
  metadata?: {
    channel?: 'email' | 'linkedin' | 'phone' | 'other';
    messageId?: string;
    /** Points to the canonical investors/{investorId}/communications/{commId} doc, when sent via Graph. */
    communicationId?: string;
    subject?: string;
    actionId?: string;
    /** Human-readable audit line (e.g. confidence change); not shown in Notes UI. */
    summary?: string;
    source?: string;
    previousStatus?: string | null;
    recipient?: string;
    confidenceStars?: 1 | 2 | 3 | 4 | 5 | null;
    fieldName?: TargetInvestorOperationalField | 'sentCountAdjustment';
    previousValue?: string | null;
    previousLabel?: string | null;
    newValue?: string | null;
    newLabel?: string | null;
    bulkOperationId?: string;
    progressTransition?: 'manual' | 'email' | 'stage4_seed' | 'migration';
  };
}

export interface TargetInvestorResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  errorCode?: string;
  retryable?: boolean;
}

export interface TargetInvestorSeedResult {
  totalMatches: number;
  created: number;
  skippedExisting: number;
  updatedExisting?: number;
}

export interface InvestorCampaignLink {
  clientId: string;
  clientName: string;
  targetInvestorId: string;
  outreachStatus?: string | null;
  raiseCycle?: TargetInvestorRaiseCycle | null;
  raiseAmount?: number | null;
  lastContactAt?: Timestamp | null;
  updatedAt?: Timestamp;
}
